import { BaseButton } from "@/components/ui-kit/Buttons/BaseButton";
import { ButtonType } from "@/types/ui-kit/buttons";
import React, { useEffect, useState } from "react";
import { BaseDialogModalWindow } from "@/components/modules/Post/BaseDialogModalWindow";
import { useRouter } from "next-translate-routes";
import { getDomainUrl } from "@/utils/getDomainUrl";
import RempApi from "@/connectors/remp/api/RempApi";
import { UnlockedArticle } from "@/connectors/remp/types/rempTypes";
import { WarningDialogModalWindow } from "@/components/modules/Post/WarningDialogModalWindow";
import { useIsAppOnline } from "@/hooks/useIsAppOnline";
import { pushToDataLayer } from "@/utils/pushToDataLayer";

export const CopyUrlDialogModalWindow: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}> = ({ isOpen, onClose, title }) => {
  const [successMessage, setSuccessMessage] = useState(false);
  const isAppOnline = useIsAppOnline();
  const [availableMobileSharing, setAvailableMobileSharing] = useState(false);
  const [userCanShareArticle, setUserCanShareArticle] = useState(true);

  const router = useRouter();

  const [shareData, setShareData] = useState({
    title: title ?? "Magazín Reportér",
    url: "",
  });

  useEffect(() => {
    async function unlockArticle() {
      const api = new RempApi();
      await api.unlockArticle(router.asPath);

      if (api.result === undefined) {
        setUserCanShareArticle(false);
        return;
      }

      setShareData({
        title: title ?? "Magazín Reportér",
        url: (api.result as UnlockedArticle)?.token
          ? getDomainUrl(`${(api.result as UnlockedArticle)?.url}?ast=${(api.result as UnlockedArticle)?.token}`)
          : "",
      });
    }

    if (isAppOnline) {
      unlockArticle();
    }
  }, [isAppOnline, router.asPath, title]);

  useEffect(() => {
    setAvailableMobileSharing(navigator.canShare && navigator.canShare(shareData));
  }, [shareData]);

  const handleCopyURL = () => {
    if (navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareData.url).then(() => setSuccessMessage(true));
    }
    pushToDataLayer({
      event: "article_interaction",
      interaction_type: "click_cta",
      interaction_option: "Kopírovat odkaz",
    });
  };

  const handleMobileSharingURL = async () => {
    if (availableMobileSharing) {
      try {
        await navigator.share(shareData);
        pushToDataLayer({
          event: "article_interaction",
          interaction_type: "share",
          interaction_option: title ?? "Magazín Reportér",
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  return (
    <>
      {userCanShareArticle ? (
        <BaseDialogModalWindow isOpen={isOpen} onClose={onClose}>
          <h3 className="mb-[8px] font-mercury text-xl font-semibold text-dark">Odemkněte článek pro přátele</h3>
          <div className="mb-[24px]">
            <p className="font-body font-light text-dark-70">
              Tento článek můžete odemknout pro své přátele, aby si ho zdarma přečetli. Pošlete jim tento speciální
              odkaz:
            </p>
          </div>
          {!availableMobileSharing && (
            <div className="mb-[24px]">
              <input
                type="text"
                className="w-full rounded-sm border-gray-200 px-[12px] py-[8px] font-condensed font-light dark:border-white-25 dark:bg-transparent dark:text-white dark:placeholder:text-white-70"
                value={shareData.url}
                readOnly
              />
            </div>
          )}
          <div className="flex w-full flex-col gap-[16px] md:flex-row">
            <div className="w-full overflow-hidden rounded-full">
              <BaseButton
                href="#"
                title={`${!availableMobileSharing ? "Kopírovat odkaz" : "Odemknout a sdílet"}`}
                type={ButtonType.Primary}
                onClick={!availableMobileSharing ? handleCopyURL : handleMobileSharingURL}
              />
            </div>
          </div>
          {!availableMobileSharing && successMessage && (
            <div className="mt-[24px]">
              <p className="font-sans text-sm text-green">Odkaz byl zkopírován.</p>
            </div>
          )}
          <div className="mt-[24px]">
            <p className="font-body font-light text-dark-70">
              Text se obdarovanému odemkne po registraci nebo přihlášení se na webu Reportérmagazín.
            </p>
          </div>
        </BaseDialogModalWindow>
      ) : (
        <WarningDialogModalWindow
          title="To je nám líto"
          text="Daný článek již nesplňuje podmínky pro otevření zdarma"
          button={<BaseButton onClick={onClose} title="Zavřít" type={ButtonType.Primary} />}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
    </>
  );
};
